import { useState } from 'react';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/context/authz';
import { useToast } from '@/context/ToastContext';
import {
  useAcceptLineSplitProposal,
  useDismissLineSplitProposal,
} from '@/api/rpc/hooks/accounts-payable/useLineSplitProposalActions';

/**
 * AP's answers to the suggested splits of an invoice: accepting or dismissing
 * one line's suggestion, who may, which line is in flight, and the toast that
 * says how it went.
 */
export function useSplitProposalAnswers({
  invoiceId,
  canEditMatches,
}: {
  invoiceId: string;
  /** The invoice is still draft or matched, so a suggestion can be answered. */
  canEditMatches: boolean;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [canUpdateInvoice] = useCan(Action.Update, Subject.APInvoice);
  const acceptProposal = useAcceptLineSplitProposal();
  const dismissProposal = useDismissLineSplitProposal();
  const [pendingLineId, setPendingLineId] = useState<string | null>(null);

  const disabledReason = !canEditMatches
    ? gt('Only draft or matched invoices can be changed.', {
        context: 'tooltip on a disabled button of a suggested split',
      })
    : !canUpdateInvoice
      ? gt('You do not have permission to change this invoice.', {
          context: 'tooltip on a disabled button of a suggested split',
        })
      : null;

  const answer = (lineId: string, action: 'accept' | 'dismiss'): void => {
    const mutation = action === 'accept' ? acceptProposal : dismissProposal;
    const doneMessage =
      action === 'accept'
        ? gt('Split accepted', {
            context: 'toast after accepting a suggested split',
          })
        : gt('Split dismissed', {
            context: 'toast after dismissing a suggested split',
          });
    setPendingLineId(lineId);
    mutation.mutate(
      { apInvoiceId: invoiceId, lineItemId: lineId },
      {
        onSuccess: () => showToast({ message: doneMessage, color: 'success' }),
        onError: (error) =>
          showToast({ message: error.message, color: 'error' }),
        onSettled: () => setPendingLineId(null),
      },
    );
  };

  return {
    disabledReason,
    pendingLineId,
    accept: (lineId: string) => answer(lineId, 'accept'),
    dismiss: (lineId: string) => answer(lineId, 'dismiss'),
  };
}
